import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  Input,
  Spinner,
  useColorModeValue,
  HStack,
  Icon,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaBuilding, FaSearch } from 'react-icons/fa';
import { useGetDataRecords } from '../../_Hooks/useGetDataRecords';

const STORAGE_KEY = 'WAM_SELECTED_CONSORCIO_ID';

const SIN_CONSORCIOS = [];

// value: id elegido desde afuera (p. ej. el selector del asistente); cuando cambia, el
// switcher lo adopta sin volver a avisar por onChange. onListaCargada recibe la lista
// para que otros componentes no la vuelvan a pedir.
const ConsorcioSwitcher = ({ onChange, value = null, onListaCargada }) => {
  const { useDataRecords } = useGetDataRecords('CONSORTIUMS__CONSORTIUM_GET_LIST', 1, 200);
  const consorcios = Array.isArray(useDataRecords?.data) ? useDataRecords.data : SIN_CONSORCIOS;
  const isLoading = useDataRecords?.isLoading;
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });
  const [reportedId, setReportedId] = useState(null);
  const valorAnterior = useRef(value);

  useEffect(() => {
    if (valorAnterior.current === value) return;
    valorAnterior.current = value;
    if (value == null || Number(value) === Number(selectedId)) return;
    setSelectedId(Number(value));
    setReportedId(Number(value));
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {}
  }, [value, selectedId]);

  useEffect(() => {
    if (!isLoading) onListaCargada?.(consorcios);
  }, [consorcios, isLoading, onListaCargada]);

  useEffect(() => {
    if (!consorcios.length) return;
    if (selectedId && consorcios.some((c) => Number(c.id) === Number(selectedId))) return;
    const first = consorcios[0];
    if (first) {
      setSelectedId(first.id);
      try {
        localStorage.setItem(STORAGE_KEY, String(first.id));
      } catch {}
    }
  }, [consorcios, selectedId, onChange]);

  const selectedConsorcio = useMemo(
    () => consorcios.find((c) => Number(c.id) === Number(selectedId)) || null,
    [consorcios, selectedId]
  );

  useEffect(() => {
    if (selectedConsorcio && reportedId !== selectedConsorcio.id) {
      onChange?.(selectedConsorcio);
      setReportedId(selectedConsorcio.id);
    }
  }, [selectedConsorcio, reportedId, onChange]);

  const filtered = useMemo(() => {
    if (!search) return consorcios;
    const term = search.toLowerCase();
    return consorcios.filter((cons) => {
      const label = `${cons?.domicilio || ''} ${cons?.nombre || ''}`.toLowerCase();
      return label.includes(term);
    });
  }, [consorcios, search]);

  const handleSelect = (consorcio) => {
    setSelectedId(consorcio.id);
    try {
      localStorage.setItem(STORAGE_KEY, String(consorcio.id));
    } catch {}
    onChange?.(consorcio);
  };

  const labelColor = useColorModeValue('gray.600', 'gray.300');
  const buttonBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.200');
  const buttonHover = useColorModeValue('blackAlpha.100', 'whiteAlpha.300');
  const menuBg = useColorModeValue('white', 'gray.800');
  const menuBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const activeItemBg = useColorModeValue('blue.50', 'whiteAlpha.200');
  const hoverItemBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const isCompact = useBreakpointValue({ base: true, md: false });

  return (
    <Menu>
      <MenuButton
        px={3}
        py={2}
        borderRadius="xl"
        bg={buttonBg}
        _hover={{ bg: buttonHover }}
        fontSize="sm"
        aria-label="Cambiar consorcio"
      >
        <HStack spacing={2}>
          <Icon as={FaBuilding} />
          {!isCompact && (
            <Box textAlign="left">
              <Text fontSize="xs" color={labelColor}>
                Consorcio
              </Text>
              <Text fontWeight="semibold" noOfLines={1}>
                {selectedConsorcio?.domicilio || selectedConsorcio?.nombre || 'Seleccionar'}
              </Text>
            </Box>
          )}
        </HStack>
      </MenuButton>
      <MenuList minW="280px" maxH="320px" overflow="hidden" p={3} bg={menuBg} borderColor={menuBorder}>
        <InputGroup mb={3}>
          <InputLeftElement pointerEvents="none" color={mutedText}>
            <FaSearch />
          </InputLeftElement>
          <Input
            placeholder="Buscar consorcio..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="sm"
            borderRadius="lg"
            bg={inputBg}
            borderColor={menuBorder}
            _placeholder={{ color: mutedText }}
          />
        </InputGroup>

        <Box maxH="220px" overflowY="auto">
          {isLoading && (
            <HStack justify="center" py={6}>
              <Spinner size="sm" />
              <Text fontSize="sm" color={labelColor}>Cargando consorcios…</Text>
            </HStack>
          )}

          {!isLoading && filtered.length === 0 && (
            <Box py={4} textAlign="center">
              <Text fontSize="sm" color={mutedText}>
                Sin resultados
              </Text>
            </Box>
          )}

          {filtered.map((cons) => (
            <MenuItem
              key={cons.id}
              onClick={() => handleSelect(cons)}
              borderRadius="lg"
              fontSize="sm"
              bg={Number(cons.id) === Number(selectedId) ? activeItemBg : 'transparent'}
              _hover={{
                bg: hoverItemBg,
              }}
            >
              <Box>
                <Text fontWeight="medium">{cons.domicilio || cons.nombre || `Consorcio #${cons.id}`}</Text>
                <Text fontSize="xs" color={mutedText}>
                  UF: {cons?.cantidad_uf || cons?.cantidad_unidades || '—'}
                </Text>
              </Box>
            </MenuItem>
          ))}
        </Box>
      </MenuList>
    </Menu>
  );
};

export default ConsorcioSwitcher;
